import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    DATE_FORMAT,
    hasAccess,
    httpRequest,
    onRequestError,
    printLog,
} from "../../helper/global";
import HomeAppBar from "../../components/HomeAppBar/HomeAppBar";
import {
    Avatar,
    Box,
    Button,
    Container,
    IconButton,
    Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { showAlert } from "../../reducers/alertSlice";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckIcon from "@mui/icons-material/Check";

import { hideLoader, showLoader } from "../../reducers/loaderSlice";
import dayjs from "dayjs";
import PatientTab from "../../components/PatientTab/PatientTab";
import { setPatient } from "../../reducers/patientSlice";
import { PERMISSIONS } from "../../helper/constant";

export default function Patient({ theme, user, permissionConfig }) {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const containerStyle = { ...theme.container };
    containerStyle.p = "0px !important";
    containerStyle.m = "0px !important";
    containerStyle.pt = "65px !important";

    const patient = useSelector((state) => state.patient.info);
    const patientId = useSelector((state) =>
        state.patient.info ? state.patient.info.id : null,
    );

    //required for weight history component
    const [startDate, setStartDate] = useState(dayjs().subtract(14, "day"));
    const [endDate, setEndDate] = useState(dayjs());
    const [healthData, setHealthData] = useState([]);

    const [weightDateRange, setWeightDateRange] = useState("all");

    const fetchPatientInfo = useCallback(
        async (id, weightDateRange = "all") => {
            try {
                dispatch(showLoader({ message: "Getting patient info..." }));
                const result = await httpRequest(
                    `patient/id/${id}`,
                    { weightDateRange },
                    "GET",
                    (error) => {
                        dispatch(hideLoader());
                        onRequestError(dispatch, navigate, {
                            title: error.title,
                            message: error.message,
                        });
                    },
                );

                dispatch(hideLoader());
                if (result === false) {
                    return;
                }

                printLog(result.payload);

                dispatch(setPatient(result.payload));
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        },
        [dispatch],
    );

    const verifyPatientAccount = useCallback(
        async (id) => {
            dispatch(
                showAlert({
                    title: "Confirm",
                    message: `Do you want to verify this account?`,
                    buttons: [
                        {
                            label: "Verify",
                            callback: async () => {
                                try {
                                    dispatch(
                                        showLoader({
                                            message:
                                                "Verifying patient account...",
                                        }),
                                    );
                                    const result = await httpRequest(
                                        `patient/verify/${id}`,
                                        {},
                                        "GET",
                                        (error) => {
                                            dispatch(hideLoader());
                                            onRequestError(dispatch, navigate, {
                                                title: error.title,
                                                message: error.message,
                                            });
                                        },
                                    );

                                    dispatch(hideLoader());
                                    if (result === false) {
                                        return;
                                    }

                                    fetchPatientInfo(id, weightDateRange);

                                    dispatch(
                                        showAlert({
                                            title: "",
                                            message: `Account has been verified successfully.`,
                                        }),
                                    );
                                } catch (error) {
                                    console.error(
                                        "Error fetching data:",
                                        error,
                                    );
                                }
                            },
                        },
                        { label: "Cancel" },
                    ],
                }),
            );
        },
        [dispatch, weightDateRange],
    );

    useEffect(() => {
        fetchPatientInfo(id, weightDateRange);
    }, [id]);

    const handleOnStartDateChange = (newValue) => {
        setStartDate(newValue);
    };

    const handleOnEndDateChange = (newValue) => {
        setEndDate(newValue);
    };

    const handleWeightDateRangeChange = (newValue) => {
        setWeightDateRange(newValue);
        fetchPatientInfo(id, newValue);
    };

    const fetchHealthData = useCallback(
        async (patientId, startDate, endDate) => {
            if (!user || !permissionConfig) {
                return;
            }

            if (
                !hasAccess(
                    user,
                    permissionConfig,
                    "healthData",
                    PERMISSIONS.read,
                )
            ) {
                return;
            }

            try {
                dispatch(showLoader({ message: "Getting health data..." }));
                const result = await httpRequest(
                    `health/data/${patientId}`,
                    {
                        startDate: startDate
                            ? startDate.format(DATE_FORMAT.defaultDate)
                            : null,
                        endDate: endDate.format(DATE_FORMAT.defaultDate),
                    },
                    "GET",
                    (error) => {
                        dispatch(hideLoader());
                        onRequestError(dispatch, navigate, {
                            title: error.title,
                            message: error.message,
                        });
                    },
                );

                dispatch(hideLoader());

                if (result === false) {
                    return;
                }

                setHealthData(result.payload);
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        },
        [user, permissionConfig],
    );

    useEffect(() => {
        if (!patient) {
            return;
        }

        fetchHealthData(patient.id, startDate, endDate);
    }, [startDate, endDate, patient]);

    return (
        <>
            <Container sx={containerStyle}>
                <HomeAppBar
                    theme={theme}
                    selectedPage="patients"
                    user={user}
                    permissionConfig={permissionConfig}
                />

                {patient && (
                    <Box sx={{ p: { xs: 1, md: 0 } }}>
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{
                                position: "sticky",
                                top: 60,
                                backgroundColor: "background.default",
                                zIndex: 10,
                                py: 2,
                                px: 2,
                                mb: 0,
                            }}
                        >
                            <Box display="flex" alignItems="center">
                                <IconButton
                                    aria-label="back"
                                    size="large"
                                    onClick={() => {
                                        navigate("/patients");
                                    }}
                                >
                                    <ArrowBackIosIcon />
                                </IconButton>
                                <Box display="flex" alignItems="center">
                                    <Avatar
                                        alt={`${patient?.firstName} ${
                                            patient?.lastName ?? ""
                                        }`}
                                        src={
                                            patient?.avatar ??
                                            patient?.firstName
                                        }
                                        sx={{ mr: 1 }}
                                    />
                                    <Typography variant="h5">
                                        {patient
                                            ? `${patient.firstName} ${patient.lastName}`
                                            : ""}
                                    </Typography>

                                    {patient.signInService === "Email" &&
                                        !patient.lastLogin &&
                                        !patient.adminVerified && (
                                            <Button
                                                variant="contained"
                                                size="small"
                                                startIcon={<CheckIcon />}
                                                sx={{ ml: 2 }}
                                                onClick={() => {
                                                    verifyPatientAccount(id);
                                                }}
                                            >
                                                Verify Account
                                            </Button>
                                        )}
                                </Box>
                            </Box>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<RefreshIcon />}
                                sx={{ mr: 3 }}
                                onClick={() => {
                                    fetchPatientInfo(id, weightDateRange);
                                }}
                            >
                                Data Update
                            </Button>
                        </Box>

                        <Box>
                            <PatientTab
                                theme={theme}
                                user={user}
                                patient={patient}
                                startDate={startDate}
                                endDate={endDate}
                                handleOnStartDateChange={
                                    handleOnStartDateChange
                                }
                                handleOnEndDateChange={handleOnEndDateChange}
                                healthData={healthData}
                                permissionConfig={permissionConfig}
                                weightDateRange={weightDateRange}
                                handleWeightDateRangeChange={
                                    handleWeightDateRangeChange
                                }
                            />
                        </Box>
                    </Box>
                )}
            </Container>
        </>
    );
}
